















































import SfProductCard from "~/themes/fiskarsMoominDefault/components/components/organisms/SfProductCard/SfProductCard.vue";
import SfCarousel from "~/themes/fiskarsMoominDefault/components/components/organisms/SfCarousel/SfCarousel.vue";
import {defineComponent, ref, computed, onMounted, watch, onBeforeUnmount} from "@nuxtjs/composition-api";
import {coveoGetters, useCoveoHeadlessProductListing} from "~/composables";
import { PropType } from "vue";
import { CoveoCategoryInterface, CoveoCollectionInterface } from "types/index";
import {loadAdvancedSearchQueryActions, loadSearchActions, loadSearchAnalyticsActions} from "@coveo/headless";
import {getCMSItem} from "~/helpers/globalLabels";

export default defineComponent({
  name: "CategoryProductSlider",
  components: {
    SfCarousel,
    SfProductCard,
  },
  props: {
    badgeColor: {
      type: Object,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    linkText: {
      type: String,
      default: "",
    },
    linkUrl: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    numberOfResults: {
      type: [Number, String],
      default: 55,
    },
    addToCart: {
      type: String,
      default: "Add To Cart",
    },
    categoryMapping: {
      type: Array as PropType<CoveoCategoryInterface[]>,
      default: [],
    },
    collectionMapping: {
      type: Array as PropType<CoveoCollectionInterface[]>,
      default: () => [{}],
    },
    link: {
      type: [Object, String],
      default: "",
    },
    classification_campaign: Object,
    classification_character: Object,
    classification_material: Object,
    classification_size: Object,
  },
  setup(props) {
    // Engine and search setup
    const { engine, resultList } = useCoveoHeadlessProductListing();
    const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
    const { executeSearch } = loadSearchActions(engine);
    
    // Reactive state variables
    const productsList = ref([]);
    const totalCountFilteredProducts = ref(0);
    const loadingProducts = ref(false);
    const facets = ref([]);
    let unsubscribeEngine, unsubscribeResultList;
    
    // UI configuration
    const carouselSettings = ref({
      type: Object,
      settings: {
        type: "carousel",
        rewind: true,
        perView: 4,
        slidePerPage: true,
        gap: 0,
        breakpoints: {
          1023: {
            perView: 2,
            peek: {
              before: 0,
              after: 0,
            },
          },
        },
      },
    });
    
    // Category and collection mapping
    const catByRef = computed(() => props?.categoryMapping?.map((category) => category?.categoryName ?? ''));
    const collectionByRef = computed(() => props?.collectionMapping?.map((collection) => collection?.collectionCode));
    const catsStringList = computed(() => catByRef.value.map(word => `"${word}"`).join(', '));
    const collectionsString = computed(() => collectionByRef.value.map(word => `"${word}"`).join(', '));
    
    // Classification references
    const classOccasionByRef = computed(() =>
      props?.classification_campaign?.items?.map((classOccasion) => `"${classOccasion?.description}"`)
    );
    const classMaterialByRef = computed(() =>
      props?.classification_material?.items?.map((classMaterial) => `"${classMaterial?.description}"`)
    );
    const classSizeByRef = computed(() =>
      props?.classification_size?.items?.map((classSize) => `"${classSize?.description}"`)
    );
    const classCharacterByRef = computed(() =>
      props?.classification_character?.items?.map((classCharacter) => `"${classCharacter?.description}"`)
    );
    
    // Classification strings
    const classOccasionString = computed(() => classOccasionByRef?.value?.toString());
    const classMaterialString = computed(() => classMaterialByRef?.value?.toString());
    const classSizeString = computed(() => classSizeByRef?.value?.toString());
    const classCharacterString = computed(() => classCharacterByRef?.value?.toString());
    
    // Advanced query construction
    const classOccasionAdvancedQuery = computed(() =>
      classOccasionString.value ? '@classification_campaign == (' + classOccasionString.value + ')' : ""
    );
    const classMaterialAdvancedQuery = computed(() =>
      classMaterialString.value ? '@classification_material == (' + classMaterialString.value + ')' : ""
    );
    const classSizeAdvancedQuery = computed(() =>
      classSizeString.value ? '@classification_size == (' + classSizeString.value + ')' : ""
    );
    const classCharacterAdvancedQuery = computed(() =>
      classCharacterString.value ? '@classification_character == (' + classCharacterString.value + ')' : ""
    );
    const catAdvancedQuery = computed(() => 
      catsStringList.value ? "@categories == (" + catsStringList.value + ")" : ""
    );
    const collectionsAdvancedQuery = computed(() =>
      collectionsString.value ? " @collectioncode == (" + collectionsString.value + ")" : ""
    );
    
    // Combined queries
    const advancedQueries = computed(() => [
      classOccasionAdvancedQuery.value,
      classMaterialAdvancedQuery.value,
      classSizeAdvancedQuery.value,
      classCharacterAdvancedQuery.value,
      catAdvancedQuery.value,
      collectionsAdvancedQuery.value,
    ]);
    const advancedQuery = computed(() => advancedQueries.value.filter((query) => query).join(" AND "));
    
    // Results check
    const checkResult = computed(() => productsList?.value?.length || 0);
    
    // Search functions
    const initSearch = async () => {
      await engine.dispatch(loadAdvancedSearchQueryActions(engine).updateAdvancedSearchQueries({aq: advancedQuery.value}));
      await engine.dispatch(executeSearch(logInterfaceLoad()));
    };
    
    const unsubscribeSearch = () => {
      engine.dispatch(loadAdvancedSearchQueryActions(engine).updateAdvancedSearchQueries({aq: ''}));
      if (unsubscribeEngine) unsubscribeEngine();
      if (unsubscribeResultList) unsubscribeResultList();
    };
    
    const initSearchOnMount = () => {
      unsubscribeEngine = engine.subscribe(() => {
        loadingProducts.value = engine?.state?.search?.isLoading;
        facets.value = coveoGetters.getFacets(engine?.state?.search?.response);
        totalCountFilteredProducts.value = engine?.state?.search?.response?.totalCountFiltered;
      });

      unsubscribeResultList = resultList.subscribe(() => {
        productsList.value = coveoGetters.getFilteredProducts(resultList.state);
      });

      initSearch();
      watch([catAdvancedQuery, collectionsAdvancedQuery], async () => await initSearch(), {immediate: true});
    };
    
    // UI helper functions
    const getBadgeColor = () => {
      return getCMSItem('badge-color-discount-percentage') || "#FBAFA0";
    };
    
    // Lifecycle hooks
    onMounted(() => initSearchOnMount());
    onBeforeUnmount(() => unsubscribeSearch());
    
    return {
      getBadgeColor,
      carouselSettings,
      productsList,
      coveoGetters,
      checkResult
    };
  },
});
